import React, { useState, useEffect } from 'react';
import './OperatorDashboard.css';

function OperatorDashboard() {
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState([]);
  const [activityData, setActivityData] = useState(null);
  const [dateRange, setDateRange] = useState('today');
  const [expandedOperator, setExpandedOperator] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [dateRange]);

  const fetchData = async () => {
    try {
      const [opsRes, activityRes] = await Promise.all([
        fetch('/api/operators'),
        fetch(`/api/operator-activity?range=${dateRange}`)
      ]);

      const ops = await opsRes.json();
      const activity = await activityRes.json();

      setOperators(ops.filter(o => o.is_active));
      setActivityData(activity);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching operator data:', err);
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getOperatorActivity = (operatorName) => {
    if (!activityData?.byOperator) return null;
    return activityData.byOperator[operatorName] || null;
  };

  const getCurrentStatus = (operatorName) => {
    if (!activityData?.currentlyWorking) return null;
    return activityData.currentlyWorking.find(w => w.scanned_by === operatorName);
  };

  const toggleExpand = (operatorName) => {
    setExpandedOperator(expandedOperator === operatorName ? null : operatorName);
  };

  // Sort operators: currently working first, then by units descending
  const sortedOperators = [...operators].sort((a, b) => {
    const aWorking = getCurrentStatus(a.display_name) ? 1 : 0;
    const bWorking = getCurrentStatus(b.display_name) ? 1 : 0;
    if (bWorking !== aWorking) return bWorking - aWorking;

    const aActivity = getOperatorActivity(a.display_name);
    const bActivity = getOperatorActivity(b.display_name);
    const aUnits = aActivity?.totalUnits || 0;
    const bUnits = bActivity?.totalUnits || 0;
    return bUnits - aUnits;
  });

  // Get all departments from activity data
  const getDepartments = () => {
    const depts = new Set();
    if (activityData?.byOperator) {
      Object.values(activityData.byOperator).forEach(op => {
        if (op.byDepartment) {
          Object.keys(op.byDepartment).forEach(d => depts.add(d));
        }
      });
    }
    return Array.from(depts).sort();
  };

  const departments = getDepartments();
  const colCount = 5 + departments.length;

  if (loading) {
    return (
      <div className="operator-dashboard loading">
        <div className="spinner"></div>
        <p>Loading operator activity...</p>
      </div>
    );
  }

  return (
    <div className="operator-dashboard">
      <div className="dashboard-header">
        <h1>Operator Activity</h1>
        <div className="date-range-selector">
          <button
            className={dateRange === 'today' ? 'active' : ''}
            onClick={() => setDateRange('today')}
          >
            Today
          </button>
          <button
            className={dateRange === 'week' ? 'active' : ''}
            onClick={() => setDateRange('week')}
          >
            This Week
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {activityData?.summary && (
        <div className="summary-row">
          <div className="summary-stat">
            <span className="summary-value">{activityData.summary.totalCarts}</span>
            <span className="summary-label">Carts</span>
          </div>
          <div className="summary-stat">
            <span className="summary-value">{(activityData.summary.totalUnits || 0).toLocaleString()}</span>
            <span className="summary-label">Units</span>
          </div>
          <div className="summary-stat">
            <span className="summary-value">{activityData.summary.activeOperators}</span>
            <span className="summary-label">Active</span>
          </div>
        </div>
      )}

      {/* Operator Table */}
      <div className="operator-table-container">
        <table className="operator-table">
          <thead>
            <tr>
              <th>Operator</th>
              <th>Status</th>
              <th>Carts</th>
              <th>Units</th>
              <th>Time</th>
              {departments.map(dept => (
                <th key={dept} className="dept-col">{dept}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedOperators.map(op => {
              const activity = getOperatorActivity(op.display_name);
              const currentWork = getCurrentStatus(op.display_name);
              const isExpanded = expandedOperator === op.display_name;
              const hasCarts = activity?.carts?.length > 0;

              return (
                <React.Fragment key={op.id}>
                  <tr
                    className={`${currentWork ? 'working' : ''} ${hasCarts ? 'expandable' : ''} ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => hasCarts && toggleExpand(op.display_name)}
                  >
                    <td className="operator-name">
                      {hasCarts && <span className="expand-icon">{isExpanded ? '-' : '+'}</span>}
                      {op.display_name}
                    </td>
                    <td>
                      {currentWork ? (
                        <span className="status-badge active">Working</span>
                      ) : activity ? (
                        <span className="status-badge idle">Idle</span>
                      ) : (
                        <span className="status-badge inactive">-</span>
                      )}
                    </td>
                    <td>{activity?.cartsCompleted || '-'}</td>
                    <td>{activity?.totalUnits?.toLocaleString() || '-'}</td>
                    <td>{formatDuration(activity?.totalMinutes)}</td>
                    {departments.map(dept => {
                      const deptData = activity?.byDepartment?.[dept];
                      if (!deptData) {
                        return <td key={dept} className="dept-col">-</td>;
                      }
                      const unitsPerHour = deptData.totalMinutes > 0 && deptData.units > 0
                        ? Math.round((deptData.units / deptData.totalMinutes) * 60)
                        : null;
                      return (
                        <td key={dept} className="dept-col">
                          <span className="dept-carts">{deptData.carts}</span>
                          <span className="dept-rate">{unitsPerHour || '-'}</span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Expanded cart details */}
                  {isExpanded && activity?.carts && (
                    <tr className="cart-details-row">
                      <td colSpan={colCount}>
                        <div className="cart-details">
                          <table className="cart-details-table">
                            <thead>
                              <tr>
                                <th>Cart</th>
                                <th>Dept</th>
                                <th>WO</th>
                                <th>SKU</th>
                                <th>Units</th>
                                <th>Time</th>
                                <th>In</th>
                                <th>Out</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activity.carts
                                .sort((a, b) => new Date(b.scannedOutAt) - new Date(a.scannedOutAt))
                                .map((cart, idx) => (
                                  <tr key={idx}>
                                    <td className="cart-barcode">{cart.cartBarcode || '-'}</td>
                                    <td>{cart.department || '-'}</td>
                                    <td>{cart.workOrderNumber || '-'}</td>
                                    <td>{cart.itemSku || '-'}</td>
                                    <td>{cart.units?.toLocaleString() || '-'}</td>
                                    <td>{formatDuration(cart.elapsedMinutes)}</td>
                                    <td>{formatTime(cart.scannedInAt)}</td>
                                    <td>{formatTime(cart.scannedOutAt)}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {operators.length === 0 && (
        <div className="no-data">No active operators found</div>
      )}
    </div>
  );
}

export default OperatorDashboard;
